/**
 * Progress state.
 *
 * Toggles are optimistic: local state changes immediately so the checkbox never
 * lags a click, then patches are coalesced and handed to the adapter. If the
 * adapter rejects, the optimistic entry is rolled back. That flow already looks
 * exactly like a network write, which is why swapping in a cloud adapter needs
 * no component changes.
 */

import { create } from 'zustand';
import { emptySnapshot } from '@/domain/progress.types';
import type { BackupV1, Profile, ProgressPatch, ProgressSnapshot } from '@/domain/progress.types';
import { applyPatchesTo, remapAliases, type StorageAdapter } from '@/storage/StorageAdapter';
import { aliasMap, listSets } from '@/content/registry';

const FLUSH_DELAY_MS = 120;

interface ProgressState {
  adapter: StorageAdapter | null;
  profiles: Profile[];
  activeProfileId: string | null;
  snapshot: ProgressSnapshot;
  ready: boolean;
  error: string | null;

  attach: (adapter: StorageAdapter, preferredProfileId: string | null) => Promise<void>;
  selectProfile: (profileId: string) => Promise<void>;
  refreshProfiles: () => Promise<void>;

  /** Re-read everything after the adapter's data changed underneath us. */
  adopt: () => Promise<void>;
  isDone: (setId: string, itemId: string) => boolean;
  toggle: (setId: string, itemId: string) => void;
  /** Returns the ids it actually flipped — the exact set an undo must flip back. */
  setMany: (setId: string, itemIds: readonly string[], done: boolean) => string[];
  clearSet: (setId: string) => Promise<void>;
  pruneOrphans: () => Promise<void>;

  createProfile: (name: string) => Promise<void>;
  renameProfile: (id: string, name: string) => Promise<void>;
  duplicateProfile: (id: string, name: string) => Promise<void>;
  deleteProfile: (id: string) => Promise<void>;

  exportBackup: () => Promise<BackupV1>;
  importBackup: (backup: BackupV1, mode: 'replace' | 'merge') => Promise<void>;
}

/** Alias maps for every known set, so old ids are healed on load. */
function allAliases(): Record<string, ReadonlyMap<string, string>> {
  const out: Record<string, ReadonlyMap<string, string>> = {};
  for (const set of listSets()) out[set.id] = aliasMap(set);
  return out;
}

function liveIdsBySet(): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const set of listSets()) {
    out[set.id] = set.groups.flatMap((g) => g.items.map((i) => i.id));
  }
  return out;
}

export const useProgress = create<ProgressState>((set, get) => {
  let queue: ProgressPatch[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;
  let unsubscribe: (() => void) | null = null;
  /**
   * Guards against a second `attach` racing the first. React StrictMode
   * double-invokes mount effects, and without this both calls see an empty
   * profile list and each seeds its own "Main" — leaving one of them orphaned.
   */
  let attaching: Promise<void> | null = null;

  /** Send queued patches, rolling back the optimistic state on failure. */
  const flush = async (): Promise<void> => {
    const { adapter, activeProfileId } = get();
    const pending = queue;
    queue = [];
    timer = null;
    if (!adapter || !activeProfileId || pending.length === 0) return;

    const before = get().snapshot;
    try {
      const next = await adapter.applyPatches(activeProfileId, pending);
      set({ snapshot: next, error: null });
    } catch (err) {
      set({ snapshot: before, error: String(err) });
    }
  };

  const enqueue = (patches: ProgressPatch[]): void => {
    // Optimistic: reflect the change now, reconcile with the adapter after.
    set({ snapshot: applyPatchesTo(get().snapshot, patches) });
    queue.push(...patches);
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void flush(), FLUSH_DELAY_MS);
  };

  const watch = (profileId: string): void => {
    const { adapter } = get();
    unsubscribe?.();
    unsubscribe = adapter
      ? adapter.subscribe(profileId, (incoming) => {
          // Another tab wrote. Adopt it, but never clobber unsent local edits.
          if (queue.length === 0) set({ snapshot: remapAliases(incoming, allAliases()) });
        })
      : null;
  };

  const loadProfile = async (profileId: string): Promise<void> => {
    const { adapter } = get();
    if (!adapter) return;
    const raw = await adapter.getProgress(profileId);
    set({ snapshot: remapAliases(raw, allAliases()), activeProfileId: profileId });
    watch(profileId);
  };

  return {
    adapter: null,
    profiles: [],
    activeProfileId: null,
    snapshot: emptySnapshot('none'),
    ready: false,
    error: null,

    attach: (adapter, preferredProfileId) => {
      attaching ??= (async () => {
        set({ adapter });
        await adapter.init();
        const profiles = await adapter.listProfiles();
        const chosen =
          profiles.find((p) => p.id === preferredProfileId)?.id ?? profiles[0]?.id ?? null;
        set({ profiles });
        if (chosen) await loadProfile(chosen);
        set({ ready: true });
      })();
      return attaching;
    },

    selectProfile: async (profileId) => {
      await flush();
      await loadProfile(profileId);
    },

    refreshProfiles: async () => {
      const { adapter } = get();
      if (adapter) set({ profiles: await adapter.listProfiles() });
    },

    /**
      * A sync pull writes straight to storage, and a pull onto a fresh device
      * replaces the profile list wholesale — so re-reading only the current
      * snapshot would leave the store pointing at a profile that no longer
      * exists. Mirrors what `importBackup` does, for the same reason.
      */
    adopt: async () => {
      const { adapter, activeProfileId } = get();
      if (!adapter) return;
      const profiles = await adapter.listProfiles();
      set({ profiles });
      const next = profiles.find((p) => p.id === activeProfileId)?.id ?? profiles[0]?.id;
      if (next) await loadProfile(next);
    },

    isDone: (setId, itemId) => get().snapshot.sets[setId]?.[itemId]?.done === true,

    toggle: (setId, itemId) => {
      enqueue([{ setId, itemId, entry: { done: !get().isDone(setId, itemId) } }]);
    },

    setMany: (setId, itemIds, done) => {
      const changed = itemIds.filter((id) => get().isDone(setId, id) !== done);
      if (changed.length > 0) enqueue(changed.map((itemId) => ({ setId, itemId, entry: { done } })));
      return changed;
    },

    clearSet: async (setId) => {
      const { adapter, activeProfileId } = get();
      if (!adapter || !activeProfileId) return;
      queue = [];
      if (timer) clearTimeout(timer);
      set({ snapshot: await adapter.clearSet(activeProfileId, setId) });
    },

    pruneOrphans: async () => {
      const { adapter, activeProfileId } = get();
      if (!adapter || !activeProfileId) return;
      set({ snapshot: await adapter.pruneOrphans(activeProfileId, liveIdsBySet()) });
    },

    createProfile: async (name) => {
      const { adapter } = get();
      if (!adapter) return;
      const profile = await adapter.createProfile(name);
      await get().refreshProfiles();
      await loadProfile(profile.id);
    },

    renameProfile: async (id, name) => {
      const { adapter } = get();
      if (!adapter) return;
      await adapter.renameProfile(id, name);
      await get().refreshProfiles();
    },

    duplicateProfile: async (id, name) => {
      const { adapter } = get();
      if (!adapter) return;
      await flush();
      const profile = await adapter.duplicateProfile(id, name);
      await get().refreshProfiles();
      await loadProfile(profile.id);
    },

    deleteProfile: async (id) => {
      const { adapter } = get();
      if (!adapter) return;
      await adapter.deleteProfile(id);
      const profiles = await adapter.listProfiles();
      set({ profiles });
      const next = profiles[0];
      if (next) await loadProfile(next.id);
    },

    exportBackup: async () => {
      const { adapter } = get();
      if (!adapter) throw new Error('Storage not ready');
      await flush();
      return adapter.exportBackup();
    },

    importBackup: async (backup, mode) => {
      const { adapter, activeProfileId } = get();
      if (!adapter) throw new Error('Storage not ready');
      await adapter.importBackup(backup, mode);
      const profiles = await adapter.listProfiles();
      set({ profiles });
      const next = profiles.find((p) => p.id === activeProfileId)?.id ?? profiles[0]?.id;
      if (next) await loadProfile(next);
    },
  };
});
